import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiPlus, FiUsers, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { canStaffAccess } from '../utils/staffPages';
import toast from 'react-hot-toast';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLE = {
  active:   { background: '#dcfce7', color: '#16a34a' },
  inactive: { background: '#f3f4f6', color: '#6b7280' },
};

export default function AdminVendors() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || canStaffAccess(user?.allowedPages, 'vendors', 'edit');
  const navigate = useNavigate();
  const [vendors, setVendors]         = useState([]);
  const [types, setTypes]             = useState([]);
  const [services, setServices]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [q, setQ]                     = useState('');
  const [statusFilter, setStatus]     = useState('all');
  const [typeFilter, setTypeFilter]   = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [total, setTotal]             = useState(0);

  const fetchTypes = useCallback(async () => {
    const { data } = await API.get('/vendors/types');
    setTypes(data.types || []);
  }, []);

  const fetchServices = useCallback(async () => {
    const { data } = await API.get('/vendors/services/all');
    setServices(data.services || []);
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = { q, status: statusFilter, page, limit: 25 };
      if (typeFilter)    params.type_id    = typeFilter;
      if (serviceFilter) params.service_id = serviceFilter;
      const { data } = await API.get('/vendors', { params });
      setVendors(data.vendors || []);
      setTotalPages(data.pages || 1);
      setTotal(data.count || 0);
    } catch (err) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, typeFilter, serviceFilter, page]);

  useEffect(() => { fetchTypes(); fetchServices(); }, [fetchTypes, fetchServices]);
  useEffect(() => { setPage(1); }, [q, statusFilter, typeFilter, serviceFilter]);
  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  return (
    <div style={{ padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: '1.4rem', margin: 0 }}>Vendors</h2>
          {total > 0 && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#9ca3af' }}>{total} vendor{total !== 1 ? 's' : ''}</p>}
        </div>
        <button
          className="crm-btn crm-btn--primary"
          onClick={() => navigate('/admin/vendors/new')}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {canEdit && <><FiPlus size={15} /> Add Vendor</>}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 32px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
            placeholder="Search name, code, phone…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatus(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer' }}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer' }}
        >
          <option value="">All Types</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select
          value={serviceFilter}
          onChange={e => setServiceFilter(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer' }}
        >
          <option value="">All Services</option>
          {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="crm-card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : vendors.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>
            <FiUsers size={36} style={{ marginBottom: 10, opacity: 0.4 }} />
            <p>No vendors found</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f3f4f6', background: '#fafafa' }}>
                {['Code', 'Company', 'Type', 'Services', 'Phone', 'City', 'Outstanding', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vendors.map(v => {
                const outstanding = Number(v.outstanding || 0);
                return (
                  <tr
                    key={v.id}
                    onClick={() => navigate(`/admin/vendors/${v.id}`)}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{v.vendor_code}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: 600 }}>{v.company_name}</div>
                      {v.contact_person && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{v.contact_person}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6b7280' }}>{v.vendorType?.name || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {(v.services || []).length === 0 ? (
                        <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>
                      ) : (
                        <span style={{
                          background: '#eef2ff', color: '#6366f1',
                          fontSize: 11, fontWeight: 700,
                          padding: '3px 8px', borderRadius: 99,
                        }}>
                          {v.services.length} service{v.services.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6b7280' }}>{v.phone || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#6b7280' }}>{v.city || '—'}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: outstanding > 0 ? '#dc2626' : outstanding < 0 ? '#16a34a' : '#374151' }}>
                      {outstanding === 0 ? '—' : (outstanding > 0 ? money(outstanding) : `+${money(Math.abs(outstanding))}`)}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ ...STATUS_STYLE[v.status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>{v.status}</span>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6b7280', fontSize: 18 }}>›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="crm-btn" style={{ padding: '6px 10px' }}>
            <FiChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="crm-btn" style={{ padding: '6px 10px' }}>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}